import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext.js";
import "./StudentDashboard.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const StudentDashboard = () => {
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAvailability, setEditingAvailability] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({
    email: "",
    phone: "",
    adress: "",
    comment: ""
  });

  const days = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
  const hours = Array.from({ length: 12 }, (_, i) => i + 10); // 10-21

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Get student data linked to this user
      const studentRes = await axios.get(`${API_URL}/api/students/${user.studentId}`);
      setStudent(studentRes.data);

      // Get all coaches for display
      const coachesRes = await axios.get(`${API_URL}/api/coaches`);
      setCoaches(coachesRes.data);

      setContactForm({
        email: studentRes.data.email || "",
        phone: studentRes.data.phone || "",
        adress: studentRes.data.adress || "",
        comment: studentRes.data.comment || ""
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAvailability = (day, hour) => {
    if (!editingAvailability) return;

    const timeSlot = `${day} ${hour}`;
    const currentAvailability = student.availableTimes || [];

    const newAvailability = currentAvailability.includes(timeSlot)
      ? currentAvailability.filter(t => t !== timeSlot)
      : [...currentAvailability, timeSlot];

    setStudent({ ...student, availableTimes: newAvailability });
  };

  const handleSaveAvailability = async () => {
    try {
      await axios.put(`${API_URL}/api/students/${student._id}`, {
        availableTimes: student.availableTimes
      });
      toast.success("Verfügbarkeit gespeichert");
      setEditingAvailability(false);
      fetchData();
    } catch (error) {
      console.error("Error saving availability:", error);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleSaveContact = async () => {
    try {
      await axios.put(`${API_URL}/api/students/${student._id}`, contactForm);
      toast.success("Kontaktdaten gespeichert");
      setEditingContact(false);
      fetchData();
    } catch (error) {
      console.error("Error saving contact info:", error);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleCancelAvailability = () => {
    setEditingAvailability(false);
    fetchData();
  };

  const handleCancelContact = () => {
    setEditingContact(false);
    setContactForm({
      email: student.email || "",
      phone: student.phone || "",
      adress: student.adress || "",
      comment: student.comment || ""
    });
  };

  const isAvailable = (day, hour) => {
    if (!student || !student.availableTimes) return false;
    return student.availableTimes.includes(`${day} ${hour}`);
  };

  const isAssigned = (day, hour) => {
    if (!student) return false;
    if (Array.isArray(student.assignments) && student.assignments.length > 0) {
      return student.assignments.some(a => a.day === day && a.hour === hour);
    }
    // Fallback to legacy fields
    return student.day === day && student.hour === hour;
  };

  const getCoachName = (coachId) => {
    if (!coachId) return "-";
    const coach = coaches.find(c => String(c._id) === String(coachId));
    return coach ? `${coach.firstName} ${coach.lastName}` : "-";
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  if (!student) {
    return <div className="error">Schüler-Daten nicht gefunden</div>;
  }

  return (
    <div className="student-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Schüler Dashboard</h1>
          <p className="welcome-text">
            Willkommen, {student.firstName} {student.lastName}
          </p>
        </div>
      </div>

      {/* Current Assignments */}
      <div className="assignments-section">
        <h2>📅 Meine Kurse</h2>
        {student.assignments && student.assignments.length > 0 ? (
          <div className="assignments-grid">
            {student.assignments.map((assignment, idx) => (
              <div key={idx} className="assignment-card">
                <div className="assignment-time">
                  {assignment.day} {assignment.hour}:00 Uhr
                </div>
                <div className="assignment-coach">
                  Trainer: {getCoachName(assignment.coach)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-assignments">
            Aktuell sind Sie noch keinem Kurs zugewiesen.
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="contact-section">
        <div className="section-header">
          <h2>📞 Kontaktinformationen</h2>
          {!editingContact ? (
            <button
              className="btn-edit-small"
              onClick={() => setEditingContact(true)}
            >
              ✏️ Bearbeiten
            </button>
          ) : (
            <div className="edit-actions">
              <button className="btn-save-small" onClick={handleSaveContact}>
                ✅ Speichern
              </button>
              <button className="btn-cancel-small" onClick={handleCancelContact}>
                ❌ Abbrechen
              </button>
            </div>
          )}
        </div>

        {!editingContact ? (
          <div className="contact-info">
            <div className="info-item">
              <span className="info-label">E-Mail:</span>
              <span className="info-value">{student.email || "-"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Telefon:</span>
              <span className="info-value">{student.phone || "-"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Adresse:</span>
              <span className="info-value">{student.adress || "-"}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Kommentar:</span>
              <span className="info-value">{student.comment || "-"}</span>
            </div>
          </div>
        ) : (
          <div className="contact-form">
            <div className="form-group">
              <label>E-Mail</label>
              <input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input
                type="tel"
                value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Adresse</label>
              <input
                type="text"
                value={contactForm.adress}
                onChange={(e) => setContactForm({ ...contactForm, adress: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Kommentar</label>
              <textarea
                value={contactForm.comment}
                onChange={(e) => setContactForm({ ...contactForm, comment: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        )}
      </div>

      {/* Availability */}
      <div className="availability-section">
        <div className="section-header">
          <h2>🕒 Verfügbare Zeiten</h2>
          {!editingAvailability ? (
            <button
              className="btn-edit-small"
              onClick={() => setEditingAvailability(true)}
            >
              ✏️ Bearbeiten
            </button>
          ) : (
            <div className="edit-actions">
              <button className="btn-save-small" onClick={handleSaveAvailability}>
                ✅ Speichern
              </button>
              <button className="btn-cancel-small" onClick={handleCancelAvailability}>
                ❌ Abbrechen
              </button>
            </div>
          )}
        </div>

        {editingAvailability && (
          <div className="edit-mode-notice">
            🔧 Bearbeitungsmodus: Klicken Sie auf Zellen, um Ihre Verfügbarkeit zu ändern
          </div>
        )}

        <div className="availability-grid">
          <table className="availability-table">
            <thead>
              <tr>
                <th>Uhrzeit</th>
                {days.map(day => (
                  <th key={day}>{day.substring(0, 2)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hours.map(hour => (
                <tr key={hour}>
                  <td className="time-cell">{hour}:00</td>
                  {days.map(day => {
                    const available = isAvailable(day, hour);
                    const assigned = isAssigned(day, hour);

                    return (
                      <td
                        key={`${day}-${hour}`}
                        className={`availability-cell ${
                          assigned ? "assigned" : available ? "available" : "unavailable"
                        } ${editingAvailability && !assigned ? "editable" : ""}`}
                        onClick={() => !assigned && handleToggleAvailability(day, hour)}
                      >
                        {assigned ? "✅" : available ? "✓" : "-"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Student Info */}
      <div className="info-section">
        <h2>ℹ️ Meine Informationen</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Geburtsdatum:</span>
            <span className="info-value">{student.birthDate || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Mitglied:</span>
            <span className="info-value">{student.member ? "Ja" : "Nein"}</span>
          </div>
          {student.adult ? (
            <div className="info-item">
              <span className="info-label">Spielstärke:</span>
              <span className="info-value">{student.skillLevel || "-"}</span>
            </div>
          ) : (
            <>
              <div className="info-item">
                <span className="info-label">Trainingsgruppe:</span>
                <span className="info-value">{student.trainigGroup || "-"}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Teamspieler:</span>
                <span className="info-value">{student.team ? "Ja" : "Nein"}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
